import { css, html, LitElement, type PropertyValues } from 'lit';
import { property } from 'lit/decorators/property.js';
import { when } from 'lit/directives/when.js';
import { debounceTime, filter, fromEvent, map, switchMap } from 'rxjs';
import { QuantityController } from '../controllers/quantity.controller.js';
import { defineQuantityInputComponent } from './subcomponents/quantity-input.component.js';

defineQuantityInputComponent();

export class CartLineItemComponent extends LitElement {
  @property({ type: String, attribute: 'line-item-id' })
  lineItemId = '';

  @property({ type: Number, attribute: 'min' })
  min = 1;

  @property({ type: Number, attribute: 'max' })
  max = Number.MAX_SAFE_INTEGER;

  @property({ type: Number, attribute: 'step' })
  step = 1;

  @property({ type: Number, attribute: 'initial-quantity' })
  initialQuantity = 1;

  @property({ type: Boolean, attribute: 'allow-remove' })
  allowRemove = false;

  @property({ type: Boolean, attribute: 'allow-quantity' })
  allowQuantity = false;

  @property({ type: Boolean, attribute: 'limit-quantity-to-one' })
  limitQuantityToOne = false;

  private readonly quantityController = new QuantityController(this);

  protected override firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);

    const input = this.shadowRoot?.querySelector<HTMLElement>('quantity-input');
    const removeButton = this.shadowRoot?.querySelector<HTMLElement>('.remove-button');
    if (input) {
      fromEvent<CustomEvent>(input, 'quantitychange')
        .pipe(
          debounceTime(300),
          map((e: CustomEvent) => e.detail.quantity as number | undefined),
          filter((newQuantity: number | undefined): newQuantity is number => !!newQuantity),
          switchMap((newQuantity) => this.quantityController.updateQuantityTask.run([this.lineItemId, newQuantity])),
        )
        .subscribe();

      fromEvent(input, 'remove')
        .pipe(switchMap(() => this.quantityController.removeProductTask.run([this.lineItemId])))
        .subscribe();
    }

    if (removeButton) {
      fromEvent(removeButton, 'click')
        .pipe(switchMap(() => this.quantityController.removeProductTask.run([this.lineItemId])))
        .subscribe();
    }
  }

  override connectedCallback() {
    super.connectedCallback();

    if (this.limitQuantityToOne && this.initialQuantity > 1) {
      this.quantityController.updateQuantityTask.run([this.lineItemId, 1]);
    }
  }

  protected override render() {
    return html`
      <article>
        <figure>
          <slot name="thumbnail"></slot>
        </figure>

        <div class="details">
          <p class="title"><slot name="name"></slot></p>
          <bdi class="price"><slot name="price"></slot></bdi>

          <div class="attributes">
            <slot name="attributes"></slot>
          </div>

          ${when(this.allowRemove, () => this.renderRemoveButton())}
          ${when(
            this.allowQuantity,
            () =>
              html` <quantity-input
                class="quantity-input-wrapper"
                .quantity="${this.initialQuantity}"
                .min="${this.min}"
                .max="${this.max}"
                .step="${this.step}"
              ></quantity-input>`,
          )}
        </div>
      </article>
    `;
  }

  private renderRemoveButton() {
    return html` <button class="remove-button">&cross;</button> `;
  }

  static override get styles() {
    return [
      css`
        article {
          display: flex;
          padding-block: 0.75rem;
          gap: 0.5rem;
        }

        .details {
          display: grid;
          grid-template: 'title title remove' max-content 'attr attr attr' 1fr 'qty qty price' max-content;
          flex: 1;
          padding-block: 0.5rem;
        }

        figure {
          border-radius: 12px;
          overflow: hidden;
          max-width: 125px;
          width: 100%;
          margin: 0;
        }

        figure ::slotted(img) {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .remove-button {
          border: none;
          background: none;
          cursor: pointer;
          grid-area: remove;
          font-size: 1rem;
          height: 2em;
          width: 2em;
          align-self: center;
          justify-self: flex-end;
          color: #f30;
        }
        .title {
          grid-area: title;
          margin: 0;
        }
        .attributes {
          grid-area: attr;
        }
        .quantity-input-wrapper {
          grid-area: qty;
        }
        .price {
          grid-area: price;
          align-self: center;
          justify-self: flex-end;
        }

        ::slotted(table) {
          width: 100%;
          text-align: left;
        }
      `,
    ];
  }
}

export function defineCartLineItemComponent() {
  if (!customElements.get('y-cart-line-item')) {
    customElements.define('y-cart-line-item', CartLineItemComponent);
  }
}
